'use client';

import type { StateAbbreviation } from '@ria-client/supergraph/marketplace/types';
import type {
  CategorySection as CategorySectionType,
  ProductCard,
} from '@ria-client/supergraph/roundup/types';
import { useMemo } from 'react';
import { CategorySection } from './category-section';
import {
  createOfferOrderMap,
  filterProductCardsByOffers,
  sortProductCardsByOfferOrder,
  sortSectionsByFirstCardOrder,
} from './category-utils';
import { isFilterEnabledSlug } from './offers-filter/store/utils';

export interface ProductCardCategorySectionsProps {
  categorySections: CategorySectionType[];
  isSeoBankingFFActive: boolean;
  regionCode?: StateAbbreviation;
  showSkeleton?: boolean;
  offersListToFilterWith?: ProductCard[];
  isLoading?: boolean;
  isBankingProductCardsRedesignExperimentActive?: boolean;
  isSorted?: boolean;
  slug?: string;
}

export function ProductCardCategorySections({
  categorySections,
  isSeoBankingFFActive,
  regionCode,
  showSkeleton = false,
  offersListToFilterWith,
  isLoading,
  isBankingProductCardsRedesignExperimentActive,
  isSorted = false,
  slug,
}: ProductCardCategorySectionsProps) {
  const isFilterEnabled = isFilterEnabledSlug(slug || '');
  const filteredSections = useMemo(() => {
    if (!isFilterEnabled || !offersListToFilterWith) return categorySections;

    const offerOrderMap = createOfferOrderMap(offersListToFilterWith);
    const sectionsWithProcessedCards = categorySections
      .map(section => {
        const filteredCards = filterProductCardsByOffers(
          section.productCards,
          offersListToFilterWith,
        );
        return {
          ...section,
          productCards: isSorted
            ? sortProductCardsByOfferOrder(filteredCards, offerOrderMap)
            : filteredCards,
        };
      })
      .filter(section => section.productCards.length > 0);

    return isSorted
      ? sortSectionsByFirstCardOrder(sectionsWithProcessedCards, offerOrderMap)
      : sectionsWithProcessedCards;
  }, [categorySections, offersListToFilterWith, isFilterEnabled, isSorted]);

  let productPositionBase = -1;

  return (
    <ul className="mb-8 p-0">
      {filteredSections.map((section, sectionIndex) => {
        productPositionBase += section.productCards.length;
        return (
          <CategorySection
            description={section.description}
            eyebrowText={section.eyebrowText}
            isBankingProductCardsRedesignExperimentActive={
              isBankingProductCardsRedesignExperimentActive
            }
            isSeoBankingFFActive={isSeoBankingFFActive}
            key={sectionIndex}
            productCards={section.productCards}
            productPositionBase={productPositionBase}
            regionCode={regionCode}
            showSkeleton={showSkeleton || isLoading}
            title={section.title}
          />
        );
      })}
    </ul>
  );
}
